/**
 * Mitarbeiter-zentrierte Schicht-Listenseite.
 *
 * Liest aus dem lokalen Cache (IndexedDB), funktioniert komplett offline.
 * Sync wird zentral in der App-Hülle (`App.tsx` via `useSync`) getriggert.
 *
 * Der betrachtete Monat kommt aus dem geteilten Zeitraum-Store (store/period.ts),
 * damit Liste, Kalender und Übersicht synchron bleiben.
 */

import { useEffect, useMemo, useState } from 'react';
import { useActiveOpenShift, useDailyPayout, useDeleteShift, useSettleShift, useShiftsForMonth } from '../data/shifts';
import { useLocationsMap } from '../data/locations';
import { ShiftFormModal } from '../components/ShiftFormModal';
import { ClockInModal } from '../components/ClockInModal';
import { ViewSwitcher } from '../components/ViewSwitcher';
import { RowActionsMenu, type RowAction } from '../components/RowActionsMenu';
import { formatDate, formatMoney } from '../lib/format';
import { monthLabel } from '../lib/month';
import { usePeriod } from '../store/period';
import { isOpenEndPending, isSettleableNow, shiftHours, summarizeSettlement, summarizeSplit } from '../lib/payroll';
import { hapticError, hapticSuccess, hapticTap, hapticWarning } from '../lib/haptics';
import { useAuth, useCanManage, useCanManageLocation } from '../store/auth';
import type { LocalShift } from '../db/schema';
import type { LocalLocation } from '../db/schema';

export default function ShiftsPage() {
  const month = usePeriod((s) => s.anchorMonth);
  const prevMonth = usePeriod((s) => s.prev);
  const nextMonth = usePeriod((s) => s.next);
  const [creating, setCreating] = useState(false);
  const [clockingIn, setClockingIn] = useState(false);
  const [editing, setEditing] = useState<LocalShift | null>(null);
  const activeOpenShift = useActiveOpenShift();

  const shifts = useShiftsForMonth(month) ?? [];
  const locationsById = useLocationsMap();
  const me = useAuth((s) => s.user);
  // Pausen-Abzug nur, wenn der User das eingeschaltet hat
  const payrollOpts = { breakDeduction: me?.breakDeductionEnabled ?? false };
  const split = useMemo(
    () => summarizeSplit(shifts, payrollOpts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shifts, payrollOpts.breakDeduction],
  );
  const settlement = useMemo(
    () => summarizeSettlement(shifts, payrollOpts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shifts, payrollOpts.breakDeduction],
  );

  return (
    <div className="max-w-2xl lg:max-w-4xl mx-auto space-y-4 pb-24">
      <ViewSwitcher />

      {/* Monats-Navigation + Auszahlungs-Kopf */}
      <div className="flex items-center justify-between card p-3">
        <button
          onClick={prevMonth}
          className="btn-ghost px-3 py-1"
          aria-label="Vorheriger Monat"
        >
          ‹
        </button>
        <div className="text-center min-w-0">
          <p className="text-sm font-semibold">
            {monthLabel(month)}
            <span className="font-normal text-slate-400">
              {' · '}
              {shifts.length} Schichten · {split.combined.totalHours.toFixed(2).replace('.', ',')} h
            </span>
          </p>
          <p className="leading-tight" title="Auszahlung offen (noch nicht abgerechnet)">
            <span className="text-xl font-bold text-emerald-400">
              {formatMoney(settlement.open.totalWage)}
            </span>
            <span className="text-[11px] text-muted ml-1.5">Auszahlung offen</span>
          </p>
          <p className="text-[10px] text-slate-500">
            {settlement.settled.totalWage > 0 && (
              <>{formatMoney(settlement.settled.totalWage)} abgerechnet · </>
            )}
            {formatMoney(split.combined.totalWage)} gesamt
            {(split.done.totalHours > 0 || split.planned.totalHours > 0) && (
              <>
                {' · '}
                <span className="text-emerald-400">
                  {split.done.totalHours.toFixed(2).replace('.', ',')} h gearb.
                </span>
                {split.planned.totalHours > 0 && (
                  <span className="text-blue-300">
                    {' + '}
                    {split.planned.totalHours.toFixed(2).replace('.', ',')} h gepl.
                  </span>
                )}
              </>
            )}
          </p>
        </div>
        <button
          onClick={nextMonth}
          className="btn-ghost px-3 py-1"
          aria-label="Nächster Monat"
        >
          ›
        </button>
      </div>

      {shifts.length === 0 && (
        <p className="text-slate-400 text-center mt-8">
          Keine Schichten in {monthLabel(month)}.
        </p>
      )}

      <ul className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
        {shifts.map((s) => (
          <ShiftRow
            key={s.id}
            shift={s}
            location={locationsById.get(s.locationId)}
            onEdit={() => setEditing(s)}
          />
        ))}
      </ul>

      {/* Einstempel-Button: groß, prominent, nur wenn nicht schon eingestempelt */}
      {!activeOpenShift && (
        <button
          onClick={() => {
            hapticTap();
            setClockingIn(true);
          }}
          className="fixed left-4 sm:left-6 px-4 h-14 rounded-full bg-emerald-600 hover:bg-emerald-500 shadow-lg font-medium text-white z-40 inline-flex items-center gap-2"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0) + 1.5rem)' }}
          aria-label="Einstempeln"
        >
          <span aria-hidden className="text-xl">⏱</span>
          Einstempeln
        </button>
      )}

      {/* Floating Action Button — Bottom-Safe-Inset für Android-Gestenleiste */}
      <button
        onClick={() => {
          hapticTap();
          setCreating(true);
        }}
        className="fixed right-4 sm:right-6 w-14 h-14 rounded-full bg-brand hover:bg-brand-dark shadow-lg text-2xl text-white z-40"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0) + 1.5rem)' }}
        aria-label="Neue Schicht"
      >
        +
      </button>

      {clockingIn && (
        <ClockInModal
          onClose={() => setClockingIn(false)}
          onClockedIn={() => setClockingIn(false)}
        />
      )}

      {creating && (
        <ShiftFormModal
          onClose={() => setCreating(false)}
          onSaved={() => setCreating(false)}
        />
      )}
      {editing && (
        <ShiftFormModal
          shift={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ShiftRow({
  shift,
  location,
  onEdit,
}: {
  shift: LocalShift;
  location: LocalLocation | undefined;
  onEdit: () => void;
}) {
  const remove = useDeleteShift();
  const settle = useSettleShift();
  const dailyPayout = useDailyPayout();
  // 19.09.2026 (Shadow-Report „Tagesgehalt-Entnahme funktioniert nicht 100%“):
  // Das Zeitfenster für die Entnahme öffnet sich mit dem Schichtende — ohne
  // eigenen Takt rendert die Seite dann nicht neu, und der Menüpunkt taucht
  // erst nach einem Seitenwechsel auf.
  const [, setMinuteTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMinuteTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const me = useAuth((s) => s.user);
  const token = useAuth((s) => s.token);
  // Owner ODER Filialleitung genau an diesem Standort (Kapitel 1.3) — mirrort
  // die Backend-Prüfung für `settledAt` in shifts.ts, auch für die eigene Schicht.
  const canSettle = useCanManageLocation(shift.locationId);
  const isChef = useCanManage();
  const h = shiftHours(shift, { breakDeduction: me?.breakDeductionEnabled ?? false });
  // Phase G (Entscheidung #6): hourlyWage kann `null` sein, wenn eine fremde
  // Schicht ohne Lohn-Sichtbarkeit angezeigt würde — auf dieser Seite (nur
  // eigene Schichten) praktisch nie der Fall, aber defensiv trotzdem behandelt.
  const wage = shift.hourlyWage == null ? null : h * shift.hourlyWage;

  const actions: RowAction[] = [];
  // Nachtrag 11.09.2026: Tagesgehalt selbst aus der Kasse nehmen — nur mit
  // Chef-Freigabe (canTakeDailyPay), bei Stundenlohn, für eine beendete bzw.
  // heutige, synchronisierte, noch offene Schicht. Der Server prüft alles
  // erneut und rechnet den Betrag selbst.
  const canTakeDailyPayHere =
    !!me &&
    !!me.canTakeDailyPay &&
    me.salaryMode === 'hourly' &&
    shift.userId === me.id &&
    !shift.settledAt &&
    !shift.pending &&
    shift.status !== 'cancelled' &&
    shift.bonusStatus !== 'pending' &&
    !isOpenEndPending(shift) &&
    !shift.dailyPayoutAt &&
    dailyPayoutWindowOpen(shift.shiftDate, shift.startTime, shift.endTime) &&
    // Server-Grenze spiegeln (shifts.ts, DAILY_PAYOUT_MAX_HOURS): längere
    // Schichten rechnet der Chef ab — vorher erschien der Menüpunkt und der
    // Server lehnte erst beim Tippen ab.
    grossHours(shift) <= DAILY_PAYOUT_MAX_HOURS &&
    // Betrag unbekannt (Lohn noch nicht für diese Person freigeschaltet):
    // Menüpunkt trotzdem zeigen — der Server erklärt den Grund verständlich.
    (wage === null || wage > 0);
  if (canTakeDailyPayHere && token) {
    actions.push({
      label: 'Tagesgehalt entnehmen',
      onClick: () => {
        hapticWarning();
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          alert('Dafür brauchst du kurz Internet — die Entnahme wird direkt beim Server gebucht.');
          return;
        }
        const betrag = wage === null ? 'Das Tagesgehalt' : `${formatMoney(wage)} Tagesgehalt`;
        if (
          !confirm(
            `${betrag} für den ${formatDate(shift.shiftDate)} aus der Kasse nehmen?\n\nDie Schicht gilt danach als abgerechnet, der Chef wird informiert.`,
          )
        ) {
          return;
        }
        dailyPayout.mutate(
          { id: shift.id, token },
          {
            onSuccess: (saved) => {
              hapticSuccess();
              alert(
                saved.dailyPayoutAmount == null
                  ? 'Tagesgehalt vermerkt — die Schicht ist abgerechnet.'
                  : `${formatMoney(saved.dailyPayoutAmount)} als Tagesgehalt vermerkt — die Schicht ist abgerechnet.`,
              );
            },
            onError: (err) => {
              hapticError();
              const msg = err instanceof Error ? err.message : 'Entnahme fehlgeschlagen';
              // Netzfehler (fetch wirft TypeError) verständlich machen.
              alert(
                /failed to fetch|networkerror|load failed/i.test(msg)
                  ? 'Keine Verbindung zum Server — bitte mit Internet erneut versuchen.'
                  : msg,
              );
            },
          },
        );
      },
      disabled: dailyPayout.isPending,
    });
  }
  // Gesamtprüfung 11.09.2026 (spiegelt den Server): auf dieser Seite stehen
  // nur EIGENE Schichten. Die rechnet nur der Chef ab bzw. hebt die
  // Abrechnung auf — eine Tagesgeld-Entnahme an der eigenen Schicht kann auch
  // er nicht selbst stornieren. Offene Zusatzstunden-Anträge erst entscheiden.
  const mine = !!me && shift.userId === me.id;
  const settleAllowed = canSettle && (!mine || isChef) && !shift.dailyPayoutAt;
  if (
    settleAllowed &&
    shift.status !== 'cancelled' &&
    (shift.settledAt || (shift.bonusStatus !== 'pending' && isSettleableNow(shift)))
  ) {
    actions.push({
      label: shift.settledAt ? 'Abrechnung aufheben' : 'Abrechnen',
      onClick: () => settle.mutate({ id: shift.id, settled: !shift.settledAt }),
      disabled: settle.isPending,
    });
  }
  // Abgerechnete eigene Schichten ändert nur noch der Chef; eine Schicht mit
  // Tagesgeld-Entnahme gar nicht mehr selbst (Server: 409).
  const locked = mine && (!!shift.dailyPayoutAt || (!!shift.settledAt && !isChef));
  if (!locked) {
    actions.push({ label: 'Bearbeiten', onClick: onEdit });
    actions.push({
      label: 'Löschen',
      variant: 'danger',
      onClick: () => {
        hapticWarning();
        if (confirm('Schicht wirklich löschen?')) remove.mutate(shift.id);
      },
      disabled: remove.isPending,
    });
  }

  return (
    <li
      className="card p-3 flex items-center gap-3 border-l-4"
      style={{ borderLeftColor: location?.color ?? '#64748b' }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!locked) onEdit();
        }}
        onKeyDown={(e) => {
          if (!locked && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onEdit();
          }
        }}
        className="flex-1 min-w-0 cursor-pointer"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{formatDate(shift.shiftDate)}</span>
          <StatusBadge status={shift.status} />
          {shift.settledAt && (
            <span className="badge bg-emerald-500/20 text-emerald-300">
              {shift.dailyPayoutAt ? 'Tagesgehalt entnommen' : 'abgerechnet'}
            </span>
          )}
          {shift.pending && (
            <span className="badge bg-amber-500/20 text-amber-300">ungesynct</span>
          )}
        </div>
        <p className="text-sm text-slate-400 truncate">
          {location?.name ?? '?'} ·{' '}
          {isOpenEndPending(shift)
            ? `ab ${shift.startTime}`
            : `${shift.startTime}–${shift.endTime}`}
        </p>
        {isOpenEndPending(shift) ? (
          <p className="text-sm text-amber-300 font-medium">
            Endzeit noch eintragen
          </p>
        ) : (
          <p className="text-sm">
            {h.toFixed(2).replace('.', ',')} h ·{' '}
            <span className="text-emerald-400">{formatMoney(wage)}</span>
            {shift.bonusHours > 0 && (
              <span className="text-amber-300 text-xs ml-2">
                (inkl. +{shift.bonusHours.toFixed(2).replace('.', ',')} h)
              </span>
            )}
          </p>
        )}
        {shift.note && (
          <p className="text-xs text-slate-500 mt-1 italic truncate">„{shift.note}"</p>
        )}
      </div>
      <div className="shrink-0">
        <RowActionsMenu actions={actions} />
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: LocalShift['status'] }) {
  const cfg = {
    planned: { text: 'geplant', cls: 'bg-blue-500/20 text-blue-300' },
    done: { text: 'erledigt', cls: 'bg-emerald-500/20 text-emerald-300' },
    cancelled: { text: 'abgesagt', cls: 'bg-slate-500/20 text-slate-300 line-through' },
  }[status];
  return <span className={`badge ${cfg.cls}`}>{cfg.text}</span>;
}

/** Wie lang darf eine Schicht für die Selbst-Entnahme höchstens sein (Server: DAILY_PAYOUT_MAX_HOURS). */
const DAILY_PAYOUT_MAX_HOURS = 12;

/** Brutto-Dauer Start→Ende in Stunden (ohne Zusatzstunden), wie der Server sie prüft. */
function grossHours(shift: { startTime: string; endTime: string }): number {
  const toMin = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
  };
  let diff = toMin(shift.endTime) - toMin(shift.startTime);
  if (diff < 0) diff += 24 * 60;
  return diff / 60;
}

/**
 * Tagesgeld-Entnahme nur direkt nach Schichtende: Ende schon vorbei und
 * Schicht von heute bzw. (Nachtschicht) gestern — spiegelt die Server-Regel in
 * POST /api/shifts/:id/daily-payout, damit der Menüpunkt nur dann erscheint.
 */
function dailyPayoutWindowOpen(shiftDate: string, startTime: string, endTime: string): boolean {
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  const today = `${get('year')}-${get('month')}-${get('day')}`;
  const nowMin = Number(get('hour')) * 60 + Number(get('minute'));
  const toMin = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
  };
  const addDays = (iso: string, n: number) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const endDay = toMin(endTime) <= toMin(startTime) ? addDays(shiftDate, 1) : shiftDate;
  const ended = endDay < today || (endDay === today && toMin(endTime) <= nowMin);
  return ended && shiftDate >= addDays(today, -1);
}
